import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import getProductKPIDetails from "./Data/getProductKPIDetails";
import ChevronIcon from "../../Common/Svgs/ChevronIcon";
import getFormatedFloat from "../../Utils/getFormatedFloat";
import usePermission, {
  useTenantPermission,
} from "../../Core/hooks/usePermission";
import { PERMISSIONS, TENANT_PERMISSIONS } from "../Login/constants";
import { LockIcon } from "../../Common/Svgs/LockIcon";

const KPI = ({
  type = "assets",
  value = 1123,
  path = "",
  userPermission = true,
  tenantPermission = true,
}) => {
  const navigate = useNavigate();

  const [showTooltip, setShowToolip] = useState("");

  const { title, icon, bgColor, color } = getProductKPIDetails(type);

  const handleClick = () => {
    if (!path) return;
    navigate(`${path}`);
  };

  const getValue = () => {
    if (value) {
      if (type === "milk") return `${getFormatedFloat(value)} L`;
      return value;
    }

    return "--";
  };

  return (
    <div
      className="h-[92px] flex-1 bg-background-white rounded-md relative"
      style={{
        boxShadow: "2px 2px 8px 0px rgba(0, 0, 0, 0.1)",
      }}
      onMouseEnter={() => {
        if (!tenantPermission) setShowToolip(title);
      }}
      onMouseLeave={() => {
        if (!tenantPermission) setShowToolip("");
      }}
    >
      {!tenantPermission && (
        <div className="absolute z-20 w-full h-full pt-2 flex items-center justify-center cursor-pointer">
          <LockIcon size={18} className="text-dark-gray" />
        </div>
      )}

      {!tenantPermission && title === showTooltip && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute -top-11 left-[20%] 2xl:left-[30%] bg-black px-3 py-2 rounded-md"
        >
          <p className="font-poppins text-xs font-medium text-white">
            Permission does not exist
          </p>
          <div className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-black" />
        </motion.div>
      )}

      <div
        className="p-3"
        style={{ filter: !tenantPermission && "blur(1.5px)" }}
      >
        <div
          className="flex items-center gap-x-2 rounded-md py-1 px-2"
          style={{ background: bgColor }}
        >
          {icon}

          <p
            className="font-aileron font-semibold text-xs leading-[14.4px]"
            style={{ color }}
          >
            {title}
          </p>
        </div>

        <div
          className="mt-2 flex items-center max-w-max"
          role={userPermission && path ? "button" : "none"}
          onClick={handleClick}
        >
          <p className="font-poppins font-semibold text-2xl leading-[36px]">
            {getValue()}
          </p>

          {userPermission && path ? (
            <div className="ml-2">
              <ChevronIcon className="w-4 h-4 stroke-black" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ProductKPIs = ({ showListingsAndKPI }) => {
  const { allKpiCount } = useSelector((state) => state.home);

  // tenant permissions
  const hasTenantTripPermission = useTenantPermission([
    TENANT_PERMISSIONS.TRIP_MODULE_ACCESS,
  ]);

  const hasTenantAssetPermission = useTenantPermission([
    TENANT_PERMISSIONS.TRIKE_MODULE_ACCESS,
    TENANT_PERMISSIONS.POWER_AFRICA_ASSET_MODULE_ACCESS,
  ]);

  const hasTenantSessionPermission = useTenantPermission([
    TENANT_PERMISSIONS.SESSION_MODULE_ACCESS,
  ]);

  // user permissions
  const hasTripPermission = usePermission([
    PERMISSIONS.TRIP_VIEW,
    PERMISSIONS.TRIP_VIEW_AND_EDIT,
  ]);

  const hasAssetPermission = usePermission([
    PERMISSIONS.TRIKE_VIEW,
    PERMISSIONS.TRIKE_VIEW_AND_EDIT,
    PERMISSIONS.POWER_AFRICA_ASSET_VIEW,
    PERMISSIONS.POWER_AFRICA_ASSET_VIEW_AND_EDIT,
  ]);

  const hasSessionPermission = usePermission([
    PERMISSIONS.SESSION_VIEW,
    PERMISSIONS.SESSION_VIEW_AND_EDIT,
  ]);

  if (!showListingsAndKPI) return <div />;

  return (
    <div className="flex items-center gap-x-3 mb-3 w-[calc(100vw-72px-24px-24px)]">
      <KPI
        type="assets"
        value={allKpiCount?.assets}
        path="/assets?tab=trikes"
        userPermission={hasAssetPermission}
        tenantPermission={hasTenantAssetPermission}
      />

      <KPI
        type="trips"
        value={allKpiCount?.trips}
        path="/trips"
        userPermission={hasTripPermission}
        tenantPermission={hasTenantTripPermission}
      />

      <KPI
        type="sessions"
        value={allKpiCount?.sessions}
        path="/sessions"
        userPermission={hasSessionPermission}
        tenantPermission={hasTenantSessionPermission}
      />

      <KPI
        type="milk"
        value={allKpiCount?.milk || 0}
        path={false}
        userPermission={hasTripPermission}
        tenantPermission={hasTenantTripPermission}
      />
    </div>
  );
};

export default ProductKPIs;
